<script setup>
import { loadInformation } from '../utils/siteInformation'
import { ref, watch, computed } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { api } from '@/api/client'
import { EP } from '@/api/endpoints'
import HeadlineFraunces from '@/design/primitives/HeadlineFraunces.vue'
import PanelOrganic from '@/design/primitives/PanelOrganic.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import IconBack from '@/design/icons/IconBack.vue'

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------
const route = useRoute()

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------
const status   = ref('idle')  // 'idle' | 'loading' | 'ready' | 'not-found' | 'error'
const page     = ref(null)    // { title, content, updatedAt }
const errorMsg = ref('')

// ---------------------------------------------------------------------------
// Policy ribbon — standard slugs available in the admin panel
// ---------------------------------------------------------------------------
const POLICIES = [
  { slug: 'privacy', label: 'Privacy' },
  { slug: 'terms',   label: 'Terms'   },
  { slug: 'cookies', label: 'Cookies' },
]

const SLUG_TITLES = {
  privacy: 'Privacy Policy',
  terms:   'Terms of Service',
  cookies: 'Cookie Policy',
}

const activeSlug = computed(() => route.params.slug)

// Derive a display title: prefer API title, then known map, then capitalise slug
const displayTitle = computed(() => {
  if (page.value?.title) return page.value.title
  const slug = activeSlug.value ?? ''
  return SLUG_TITLES[slug] ?? (slug.charAt(0).toUpperCase() + slug.slice(1))
})

// ---------------------------------------------------------------------------
// Formatted "last updated" date
// ---------------------------------------------------------------------------
const updatedLabel = computed(() => {
  if (!page.value?.updatedAt) return null
  try {
    return new Date(page.value.updatedAt).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    })
  } catch {
    return null
  }
})

// ---------------------------------------------------------------------------
// Data fetch
// Strategy:
//   1. Try GET /api/legal/:slug — returns { content } (no title/updatedAt).
//      Treat empty content as a miss; fall through.
//   2. Fall back to GET /api/pages/:slug — returns { title, content, updatedAt }.
//   3. Both fail → not-found or error state.
// ---------------------------------------------------------------------------
async function loadPage(slug) {
  status.value   = 'loading'
  page.value     = null
  errorMsg.value = ''

  try { page.value = await loadInformation(slug); status.value = 'ready' }
  catch (e) { errorMsg.value = 'Unable to load this information. Please try again.'; status.value = 'error' }
}

function retry() {
  loadPage(activeSlug.value)
}

// React to route changes (including first mount)
watch(
  () => route.params.slug,
  (slug) => { if (slug) loadPage(slug) },
  { immediate: true },
)
</script>

<template>
  <main class="gazette">

    <!-- ================================================================== -->
    <!-- Gold-rail top — Back link                                           -->
    <!-- ================================================================== -->
    <RouterLink to="/" class="gold-rail" aria-label="Back to home">
      <IconBack class="gold-rail__icon" aria-hidden="true" />
      <span>Back to Home</span>
    </RouterLink>

    <!-- ================================================================== -->
    <!-- Page header band                                                    -->
    <!-- ================================================================== -->
    <header class="seal">
      <p class="kicker">EVOLUXU · LEGAL</p>

      <HeadlineFraunces level="h1" class="seal__title">
        {{ displayTitle }}
      </HeadlineFraunces>

      <!-- Policy ribbon -->
      <nav class="ribbon" aria-label="Policy documents">
        <template v-for="(policy, idx) in POLICIES" :key="policy.slug">
          <RouterLink
            :to="`/legal/${policy.slug}`"
            class="ribbon__link"
            :class="{ 'ribbon__link--active': policy.slug === activeSlug }"
            :aria-current="policy.slug === activeSlug ? 'page' : undefined"
          >
            {{ policy.label }}
          </RouterLink>
          <span v-if="idx < POLICIES.length - 1" class="ribbon__sep" aria-hidden="true">·</span>
        </template>
      </nav>
    </header>

    <!-- ================================================================== -->
    <!-- Loading skeleton                                                    -->
    <!-- ================================================================== -->
    <template v-if="status === 'loading'">
      <PanelOrganic tone="strong" :padded="true" class="column">
        <div class="stamp stamp--title u-shimmer" />
        <div class="stamp u-shimmer" style="width: 92%" />
        <div class="stamp u-shimmer" style="width: 78%" />
        <div class="stamp u-shimmer" style="width: 85%" />
      </PanelOrganic>
    </template>

    <!-- ================================================================== -->
    <!-- Not found                                                           -->
    <!-- ================================================================== -->
    <template v-else-if="status === 'not-found'">
      <PanelOrganic tone="strong" :padded="true" class="column passage">
        <p class="passage__label">POLICY NOT FOUND</p>
        <RouterLink to="/" class="gold-rail passage__back">
          <IconBack class="gold-rail__icon" aria-hidden="true" />
          <span>Back to Home</span>
        </RouterLink>
      </PanelOrganic>
    </template>

    <!-- ================================================================== -->
    <!-- Network error                                                       -->
    <!-- ================================================================== -->
    <template v-else-if="status === 'error'">
      <PanelOrganic tone="strong" :padded="true" class="column passage passage--error">
        <p class="passage__label">OFFLINE</p>
        <ButtonSeed variant="ghost" size="md" @click="retry">
          Retry
        </ButtonSeed>
      </PanelOrganic>
    </template>

    <!-- ================================================================== -->
    <!-- Ready — reading column                                              -->
    <!-- ================================================================== -->
    <template v-else-if="status === 'ready' && page">
      <PanelOrganic tone="strong" :padded="true" class="column u-rise-in">

        <!-- CMS content -->
        <div class="legal-body" v-html="page.content" />

        <!-- Last updated -->
        <p v-if="updatedLabel" class="last-updated">
          LAST UPDATED · {{ updatedLabel }}
        </p>

      </PanelOrganic>
    </template>

  </main>
</template>

<style scoped>
/* ============================================================
   Gazette — outer page wrapper
   (no bg — BackgroundField provides the fixed ARK panorama)
   ============================================================ */
.gazette {
  min-height: 100vh;
  padding: var(--sp-12) var(--sp-6) var(--sp-24);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-8);
}

/* ============================================================
   Gold-rail — back navigation link
   ============================================================ */
.gold-rail {
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  font-family: var(--font-display);
  font-size: var(--fs-12);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--gold-soft, rgba(229, 199, 107, 0.55));
  text-decoration: none;
  transition: color var(--t-fast) var(--ease-road);
}
.gold-rail:hover { color: var(--gold); }

.gold-rail__icon {
  width: 1em;
  height: 1em;
  flex-shrink: 0;
}

/* ============================================================
   Seal — page header band
   ============================================================ */
.seal {
  width: 100%;
  max-width: var(--measure-prose);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--sp-4);
}

/* ============================================================
   Kicker — "EVOLUXU · LEGAL"
   ============================================================ */
.kicker {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--gold);
  opacity: 0.75;
}

/* Headline sits directly inside .seal via HeadlineFraunces */
.seal__title {
  /* HeadlineFraunces already provides display font + allcaps + tight lh */
  line-height: var(--lh-tight);
}

/* ============================================================
   Ribbon — policy nav links
   ============================================================ */
.ribbon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
}

.ribbon__link {
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-soft);
  text-decoration: none;
  transition: color var(--t-fast) var(--ease-road);
}
.ribbon__link:hover { color: var(--gold); }

.ribbon__link--active {
  color: var(--gold);
  font-weight: 700;
}

.ribbon__sep {
  color: var(--gold);
  opacity: 0.4;
  user-select: none;
  font-family: var(--font-display);
  font-size: var(--fs-12);
}

/* ============================================================
   Column — main glass reading panel
   ============================================================ */
.column {
  width: 100%;
  max-width: var(--measure-prose);
}

/* ============================================================
   Skeleton stamps
   ============================================================ */
.stamp {
  height: 1rem;
  border-radius: var(--r-sm);
  margin-bottom: var(--sp-3);
  opacity: 0.4;
  /* background/animation comes from u-shimmer */
  background: var(--ash);
}
.stamp--title {
  height: 2.75rem;
  width: 60%;
  margin-bottom: var(--sp-6);
}

/* ============================================================
   Passage — 404 / error states
   ============================================================ */
.passage {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--sp-6);
}

.passage__label {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--fs-32);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink);
  line-height: var(--lh-tight);
}

.passage--error .passage__label {
  color: var(--ember);
}

.passage__back {
  /* inherits .gold-rail */
  align-self: auto;
}

/* ============================================================
   Legal body — CMS HTML prose
   ============================================================ */
.legal-body,
.legal-body :deep(*) {
  -webkit-user-select: text;
  user-select: text;
}

.legal-body {
  cursor: text;
}

.legal-body :deep(p) {
  margin: 0 0 var(--sp-4);
  line-height: var(--lh-loose);
  color: var(--ink);
  font-size: var(--fs-16);
}

.legal-body :deep(h2) {
  font-family: var(--font-display);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: var(--sp-12) 0 var(--sp-3);
  color: var(--gold);
  font-size: var(--fs-26);
  font-weight: 400;
}

.legal-body :deep(h3) {
  font-family: var(--font-display);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: var(--sp-8) 0 var(--sp-3);
  color: var(--ink);
  font-size: var(--fs-21);
  font-weight: 400;
}

.legal-body :deep(ul),
.legal-body :deep(ol) {
  margin: 0 0 var(--sp-4) var(--sp-6);
  padding-left: var(--sp-5);
  color: var(--ink);
}

.legal-body :deep(ol) { list-style-type: decimal; }
.legal-body :deep(ul) { list-style-type: disc; }
.legal-body :deep(ul ul) { list-style-type: circle; }
.legal-body :deep(ol ol) { list-style-type: lower-alpha; }
.legal-body :deep(ol ol ol) { list-style-type: lower-roman; }

.legal-body :deep(a) {
  color: var(--gold);
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* Drop cap — first letter of first paragraph */
.legal-body :deep(p:first-of-type)::first-letter {
  font-family: var(--font-display);
  font-size: 4em;
  line-height: 0.85;
  padding: 0.05em 0.18em 0 0;
  color: var(--gold);
  float: left;
}

/* ============================================================
   Last updated — Anton allcaps gold stamp
   ============================================================ */
.last-updated {
  margin: var(--sp-12) 0 0;
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gold);
  opacity: 0.65;
}
.site-contacts :deep(dt){font-weight:700;margin-top:16px}.legal-content :deep(dd){margin:4px 0 16px;white-space:pre-line}
</style>
